import { type AdFormatOption } from "./components/campaign/cards/ad-format-card";
import { type BudgetPricingOption } from "./components/campaign/cards/budget-pricing-card";
import { type BudgetPacingOption } from "./components/campaign/cards/budget-pacing-card";
import { Globe, LayoutGrid, Store, type LucideIcon } from "lucide-react";

export const formatOptions: AdFormatOption[] = [
  {
    id: "sponsored_products",
    title: "Sponsored products",
    description:
      "Your products appear natively within dispensary menus, pulled live from your catalog.",
    bullets: [
      "Auto-generated from your catalog data",
      "Cost-per-placement pricing",
      "In-stock filtering built in",
      "Live within minutes of launch",
    ],
    available: true,
  },
  {
    id: "display_banner",
    title: "Display banner",
    description:
      "Brand-supplied image or animated creatives placed in high-visibility slots.",
    bullets: [
      "Requires uploaded creative assets",
      "CPM or fixed-tenancy pricing",
      "Above-the-fold premium slots",
      "Full creative control",
    ],
    available: false,
  },
  {
    id: "featured_brand",
    title: "Featured brand",
    description:
      "A dedicated branded page within the dispensary menu showcasing your catalog.",
    bullets: [
      "Custom brand landing page",
      "Full product range showcase",
      "Real-time editable content",
      "Drives category brand recall",
    ],
    available: false,
  },
];

export const adFormatLabels = Object.fromEntries(
  formatOptions.map((option) => [option.id, option.title]),
) as Record<string, string>;

export const pacingOptions: BudgetPacingOption[] = [
  {
    id: "none",
    title: "No pacing",
    description:
      "Allow the campaign to spend its budget without a daily pacing limit.",
    available: true,
  },
  {
    id: "even",
    title: "Even",
    description:
      "Your daily budget is spread evenly across each day's available ad impressions, from open to close.",
    available: true,
  },
  {
    id: "custom",
    title: "Custom",
    description:
      "Set a daily pacing cap to control how quickly your campaign spends.",
    available: true,
  },
];

export const pricingOptions: BudgetPricingOption[] = [
  {
    id: "cpm",
    title: "CPM",
    description:
      "Pay per 1,000 impressions served. Best for awareness campaigns where reach and visibility are the primary goal - you pay whether or not a shopper taps.",
  },
  {
    id: "cpc",
    title: "CPC",
    description:
      "Pay only when a shopper taps your product. Performance-focused - budget consumed by intent, not exposure.",
  },
];

export const pricingLabels: Record<string, string> = {
  cpc: "CPC",
  cpm: "CPM",
};

export const pacingLabels: Record<string, string> = {
  none: "No pacing",
  even: "Even · per day",
  custom: "Custom",
};

export type PlacementChannelMeta = {
  title: string;
  impressionsLabel: string;
  zonesLabel: string;
  icon: LucideIcon;
  accent: string;
};

export const fallbackPlacementChannelMeta: PlacementChannelMeta = {
  title: "Placements",
  impressionsLabel: "",
  zonesLabel: "zones",
  icon: LayoutGrid,
  accent: "tw:bg-muted tw:text-foreground",
};

export const placementChannelMeta: Record<string, PlacementChannelMeta> = {
  WEB: {
    title: "Web",
    impressionsLabel: "",
    zonesLabel: "zones",
    icon: Globe,
    accent: "tw:bg-sky-100 tw:text-sky-800",
  },
  KIOSK: {
    title: "Kiosk",
    impressionsLabel: "",
    zonesLabel: "zones",
    icon: Store,
    accent: "tw:bg-muted tw:text-emerald-700",
  },
  POS: {
    title: "Point of sale",
    impressionsLabel: "",
    zonesLabel: "zones",
    icon: Store,
    accent: "tw:bg-muted tw:text-emerald-700",
  },
};
